import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { PanelModule } from 'primeng/panel';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { Observable, tap } from 'rxjs';
import { DividerModule } from 'primeng/divider';
import { ColorsEnum } from '@shared/enums/colors.enum';
import { DashboardOverviewPanelDataComponent } from '@app/areas/dashboard/components/dashboard-overview-panel-data/dashboard-overview-panel-data.component';
import {
  DashboardChartJSData,
  DashboardGraphComponent,
} from '@app/areas/dashboard/components/dashboard-graph/dashboard-graph.component';
import { ApplicationProfile, PortalTheme } from '@app/@shared/models';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';

export interface OverviewPanelData {
  header: string;
  headerColor?: ColorsEnum;
  subHeader?: string;
  subHeaderColor?: ColorsEnum;
  body: string;
  bodyColor?: string;
  subBody?: string;
  footer: string;
  subFooter?: string;
  footerColor?: ColorsEnum;
}

@Component({
  selector: 'app-dashboard-overview-panel',
  templateUrl: './dashboard-overview-panel.component.html',
  styleUrls: ['./dashboard-overview-panel.component.scss'],
  imports: [
    PanelModule,
    NgIf,
    NgForOf,
    AsyncPipe,
    DividerModule,
    DashboardOverviewPanelDataComponent,
    DashboardGraphComponent,
  ],
  standalone: true,
})
export class DashboardOverviewPanelComponent implements OnInit {
  @Input() header: string = '';
  @Input() data$!: Observable<OverviewPanelData[]>;
  @Input() showChartData: boolean = false;
  @Input() graphData$: Observable<DashboardChartJSData> | null = null;

  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);

  applicationProfile!: ApplicationProfile;
  public theme?: PortalTheme;

  private readonly cdr = inject(ChangeDetectorRef);
  protected overviews: OverviewPanelData[] = [];

  ngOnInit() {
    this.data$.subscribe((data) => {
      this.overviews = data;
      this.cdr.detectChanges();
      this.applicationProfileService.applicationProfile$
        .pipe(
          tap((appProfile: ApplicationProfile) => {
            this.applicationProfile = appProfile;
            this.theme = appProfile.PortalTheme.Theme;
            this.setCSSCustomProperties();
            this.checkTheme(data);
          })
        )
        .subscribe();
    });
  }

  private setCSSCustomProperties(): void {
    if (this.applicationProfile?.PortalTheme?.Theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty('--text--color-default', this.theme!.defaultTextColor);
      document.documentElement.style.setProperty('--header-bg-primary', this.theme!.header.backgroundColor);
      document.documentElement.style.setProperty('--dashboard-card-bg', this.theme!.dashboard.cardBackgroundColor);
      document.documentElement.style.setProperty('--dashboard-header-bg', this.theme!.dashboard.headerBackgroundColor);

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('DashboardOverviewPanelComponent');
    }
  }

  private checkTheme(data: Array<OverviewPanelData>) {
    const colorMap: Record<string, string> = {
      TOTAL: this.theme!.dashboard.sales,
      MEMBER: this.theme!.dashboard.loyalty,
      'NON-MEMBER': this.theme!.dashboard.rewards,
      'MEMBER CONVERSION': this.theme!.dashboard.sales,
      'ENROLLMENT TRANSACTIONS': this.theme!.dashboard.loyalty,
      REWARDS: this.theme!.dashboard.rewards,
    };

    data.forEach((item) => {
      if (item.header && colorMap[item.header]) {
        item.bodyColor = colorMap[item.header];
      }
    });
  }
}
